"use client";

import { cn } from "../../lib/utils";
import React, { useState, createContext, useContext, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, User, LogOut, Sun, Moon, Crown } from "lucide-react";
import { OspEntitlementStatus, OspEntitlementState } from "../../services/ospFirebaseService";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

interface Links {
    label: string;
    href: string;
    icon: React.JSX.Element | React.ReactNode;
    lottieSrc?: string;
    iconScale?: number;
    isActive?: boolean;
    onClick?: () => void;
}

interface SidebarContextProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    animate: boolean;
    user?: any;
    getRingStyles?: () => string;
    hasPro?: boolean;
    hasPlus?: boolean;
    onNavigate?: (section: string) => void;
    logout?: () => void;
    isDarkMode?: boolean;
    toggleDarkMode?: () => void;
    ospEntitlement?: OspEntitlementState | null;
    dashboardMode?: 'ZSV' | 'OSP';
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
    undefined
);

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
};

export const SidebarProvider = ({
    children,
    open: openProp,
    setOpen: setOpenProp,
    animate = true,
    user,
    getRingStyles,
    hasPro,
    hasPlus,
    onNavigate,
    logout,
    isDarkMode,
    toggleDarkMode,
    ospEntitlement,
    dashboardMode
}: {
    children: React.ReactNode;
    open?: boolean;
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    animate?: boolean;
    user?: any;
    getRingStyles?: () => string;
    hasPro?: boolean;
    hasPlus?: boolean;
    onNavigate?: (section: string) => void;
    logout?: () => void;
    isDarkMode?: boolean;
    toggleDarkMode?: () => void;
    ospEntitlement?: OspEntitlementState | null;
    dashboardMode?: 'ZSV' | 'OSP';
}) => {
    const [openState, setOpenState] = useState(false);

    const open = openProp !== undefined ? openProp : openState;
    const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

    return (
        <SidebarContext.Provider value={{ open, setOpen, animate, user, getRingStyles, hasPro, hasPlus, onNavigate, logout, isDarkMode, toggleDarkMode, ospEntitlement, dashboardMode }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const Sidebar = ({
    children,
    open,
    setOpen,
    animate,
    user,
    getRingStyles,
    hasPro,
    hasPlus,
    onNavigate,
    logout,
    isDarkMode,
    toggleDarkMode,
    ospEntitlement,
    dashboardMode
}: {
    children: React.ReactNode;
    open?: boolean;
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    animate?: boolean;
    user?: any;
    getRingStyles?: () => string;
    hasPro?: boolean;
    hasPlus?: boolean;
    onNavigate?: (section: string) => void;
    logout?: () => void;
    isDarkMode?: boolean;
    toggleDarkMode?: () => void;
    ospEntitlement?: OspEntitlementState | null;
    dashboardMode?: 'ZSV' | 'OSP';
}) => {
    return (
        <SidebarProvider open={open} setOpen={setOpen} animate={animate} user={user} getRingStyles={getRingStyles} hasPro={hasPro} hasPlus={hasPlus} onNavigate={onNavigate} logout={logout} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} ospEntitlement={ospEntitlement} dashboardMode={dashboardMode}>
            {children}
        </SidebarProvider>
    );
};

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
    return (
        <>
            <DesktopSidebar {...props} />
            <MobileSidebar {...(props as React.ComponentProps<"div">)} />
        </>
    );
};

export const DesktopSidebar = ({
    className,
    children,
    ...props
}: React.ComponentProps<typeof motion.div>) => {
    const { open, setOpen, animate } = useSidebar();
    return (
        <motion.div
            className={cn(
                "h-full py-4 hidden md:flex md:flex-col bg-sidebar dark:bg-card w-[300px] flex-shrink-0 px-2 overflow-hidden",
                className
            )}
            animate={{
                width: animate ? (open ? "300px" : "80px") : "300px",
            }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            {...props}
        >
            {children}
        </motion.div>
    );
};

export const MobileSidebar = ({
    className,
    children,
    ...props
}: React.ComponentProps<"div">) => {
    const { open, setOpen, user, getRingStyles, hasPro, hasPlus, onNavigate, logout, isDarkMode, toggleDarkMode, dashboardMode, ospEntitlement } = useSidebar();
    const [showProfileMenu, setShowProfileMenu] = React.useState(false);
    const profileMenuRef = React.useRef<HTMLDivElement>(null);

    // Close profile menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setShowProfileMenu(false);
            }
        };

        if (showProfileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showProfileMenu]);

    const handleLogout = () => {
        setShowProfileMenu(false);
        if (logout) {
            logout();
        }
    };

    const handleProfileClick = () => {
        setShowProfileMenu(false);
        if (onNavigate) {
            onNavigate('settings');
        }
    };

    return (
        <>
            <div
                className={cn(
                    "h-16 px-3 flex flex-row md:hidden items-center bg-sidebar dark:bg-card w-full gap-3"
                )}
                {...props}
            >
                {/* Hamburger Menu */}
                <Menu
                    className="text-neutral-800 dark:text-neutral-200 cursor-pointer flex-shrink-0"
                    onClick={() => setOpen(!open)}
                />

                {/* D! Logo - Replaced with Image */}
                <div className="flex items-center flex-shrink-0 h-10 w-10">
                    <img
                        src={isDarkMode ? "/materials/ZKOUSKA-01.PNG" : "/materials/ZKOUSKA-DARK-01.png"}
                        alt="Dostaň se na práva"
                        className="object-contain w-full h-full"
                    />
                </div>

                {/* Spacer */}
                <div className="flex-1" />

                {/* Theme Toggle Button */}



                {/* OSP UPGRADE BUTTON FOR MOBILE HEADER — same style as ZSV */}
                {dashboardMode === 'OSP' && !hasPro && onNavigate && (
                    <button
                        onClick={() => onNavigate('upgrade')}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300 flex-shrink-0",
                            "bg-yellow-400 hover:bg-yellow-300 border-b-[3px] border-yellow-600 active:border-b-0 active:translate-y-[3px]",
                            "text-yellow-950 font-black text-[10px] tracking-wide",
                            "shadow-none"
                        )}
                    >
                        <Crown className="h-3 w-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                            {ospEntitlement?.status === OspEntitlementStatus.TRIAL_NOT_ELIGIBLE
                                ? "ZÍSKAT PREMIUM"
                                : "PREMIUM ZDARMA"}
                        </span>
                    </button>
                )}

                {/* ZSV / GENERIC PRO BUTTON - Hide if in OSP mode to avoid clutter, or keep if user wants generic PRO */}
                {/* Only show generic PRO button if NOT in OSP mode OR if user is not eligible for OSP specific actions but still wants to upgrade? */}
                {/* User request: "on top the navbar... mobile where is hamburger menu... D! logo... profile picture" */}
                {/* If we are in OSP mode, we likely only want the OSP button. If in ZSV, the ZSV button. */}

                {dashboardMode !== 'OSP' && !ospEntitlement?.isPro && onNavigate && (
                    <button
                        onClick={() => onNavigate('upgrade')}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300 flex-shrink-0",
                            "bg-yellow-400 hover:bg-yellow-300 border-b-[3px] border-yellow-600 active:border-b-0 active:translate-y-[3px]",
                            "text-yellow-950 font-black text-[10px] tracking-wide",
                            "shadow-none"
                        )}
                    >
                        <Crown className="h-3 w-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                            {ospEntitlement?.status === OspEntitlementStatus.TRIAL_NOT_ELIGIBLE
                                ? "ZÍSKAT PREMIUM"
                                : "PREMIUM ZDARMA"}
                        </span>
                    </button>
                )}

                {/* Theme Toggle Button */}
                {toggleDarkMode && (
                    <button
                        onClick={toggleDarkMode}
                        className="p-2 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors flex-shrink-0"
                        aria-label="Toggle theme"
                    >
                        {isDarkMode ? (
                            <Sun className="h-6 w-6 text-neutral-800 dark:text-neutral-200" />
                        ) : (
                            <Moon className="h-6 w-6 text-neutral-800 dark:text-neutral-200" />
                        )}
                    </button>
                )}

                {/* User Avatar with Dropdown Menu */}
                {user && onNavigate && (
                    <div className="relative" ref={profileMenuRef}>
                        <button
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                            className="relative h-9 w-9 flex-shrink-0 focus:outline-none rounded-full"
                        >
                            {user.avatar ? (
                                <img
                                    src={user.avatar}
                                    className={cn(
                                        "h-9 w-9 rounded-full object-cover transition-transform duration-200",
                                        showProfileMenu ? "scale-95" : "hover:scale-105",
                                        getRingStyles ? getRingStyles() : ""
                                    )}
                                    alt="Avatar"
                                />
                            ) : (
                                <div className={cn(
                                    "h-9 w-9 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center transition-transform duration-200",
                                    showProfileMenu ? "scale-95" : "hover:scale-105",
                                    getRingStyles ? getRingStyles() : ""
                                )}>
                                    <User className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                                </div>
                            )}
                            {hasPro && (
                                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-gray-900 text-[10px] shadow-sm ring-1 ring-white">
                                    👑
                                </span>
                            )}
                            {!hasPro && hasPlus && (
                                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white font-bold shadow-sm ring-1 ring-white">
                                    +
                                </span>
                            )}
                        </button>

                        {/* Profile Dropdown Menu */}
                        <AnimatePresence>
                            {showProfileMenu && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute right-0 mt-2 w-48 bg-card dark:bg-card rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-[110] overflow-hidden"
                                >
                                    <button
                                        onClick={handleProfileClick}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground hover:bg-accent transition-colors"
                                    >
                                        <User className="h-5 w-5 flex-shrink-0" />
                                        <span>Můj Profil</span>
                                    </button>
                                    <div className="border-t border-border" />
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors"
                                    >
                                        <LogOut className="h-5 w-5 flex-shrink-0" />
                                        <span>Odhlásit se</span>
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
                <AnimatePresence>
                    {open && (
                        <>
                            {/* Backdrop overlay */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{
                                    duration: 0.2,
                                    ease: [0.4, 0, 0.2, 1],
                                }}
                                className="fixed inset-0 bg-black/50 z-[90]"
                                onClick={() => setOpen(false)}
                            />
                            {/* Sidebar panel */}
                            <motion.div
                                initial={{ x: "-100%", opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: "-100%", opacity: 0 }}
                                transition={{
                                    duration: 0.2,
                                    ease: [0.4, 0, 0.2, 1],
                                }}
                                className={cn(
                                    "fixed h-full w-[85%] max-w-sm inset-y-0 left-0 bg-sidebar dark:bg-card p-6 z-[100] flex flex-col overflow-hidden",
                                    "will-change-transform",
                                    className
                                )}
                                style={{ transform: "translateZ(0)" }}
                            >
                                <div
                                    className="absolute right-6 top-6 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                    onClick={() => setOpen(false)}
                                >
                                    <X />
                                </div>
                                {children}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

export const SidebarLink = ({
    link,
    className,
    ...props
}: {
    link: Links;
    className?: string;
    props?: any; // Relaxed type for LinkProps to avoid Next.js specific issues if not using Next Link
}) => {
    const { open, animate } = useSidebar();
    const [isHovered, setIsHovered] = useState(false);
    const [dotLottie, setDotLottie] = useState<any>(null);

    // Play animation on hover
    useEffect(() => {
        if (dotLottie) {
            if (isHovered) {
                dotLottie.play();
            } else {
                dotLottie.stop();
            }
        }
    }, [isHovered, dotLottie]);

    return (
        <div
            onClick={link.onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn(
                "flex items-center gap-3 group/sidebar py-3 px-2 cursor-pointer",
                "md:py-2 md:gap-2", // Smaller on desktop
                className
            )}
            {...props}
        >
            <div className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 flex items-center justify-center relative">
                {link.lottieSrc ? (
                    <div
                        className="flex items-center justify-center transition-all duration-300 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{
                            width: link.iconScale ? `${link.iconScale * 100}%` : '100%',
                            height: link.iconScale ? `${link.iconScale * 100}%` : '100%',
                            filter: (link.isActive || isHovered) ? 'none' : 'grayscale(100%)',
                            opacity: (link.isActive || isHovered) ? 1 : 0.7
                        }}
                    >
                        <DotLottieReact
                            src={link.lottieSrc}
                            loop={true}
                            autoplay={false}
                            dotLottieRefCallback={(ref) => setDotLottie(ref)}
                            {...({
                                // Force reload if src changes to handle quick switches
                                key: link.lottieSrc
                            } as any)}
                        />
                    </div>
                ) : (
                    link.icon
                )}
            </div>
            <motion.span
                animate={{
                    display: animate ? (open ? "inline-block" : "none") : "inline-block",
                    opacity: animate ? (open ? 1 : 0) : 1,
                }}
                className="text-base font-semibold group-hover/sidebar:translate-x-1 transition-transform duration-150 whitespace-pre inline-block !p-0 !m-0"
            >
                {link.label}
            </motion.span>
        </div>
    );
};
